import React from "react";
import { Reveal } from "@/components/Reveal";
import { Eyebrow } from "@/components/SectionDivider";

const PILLARS = [
  {
    title: "Moats",
    body: "Structural competitive advantages that are nearly impossible to replicate — networks, patents, scale, distribution.",
  },
  {
    title: "Pricing Power",
    body: "The ability to raise prices without losing customers — the most reliable hedge against inflation across decades.",
  },
  {
    title: "Global Scale",
    body: "Revenue from every corner of the world, not tied to any one economy or political cycle.",
  },
];

const CATEGORIES = [
  {
    label: "Payment Networks",
    body: "Processing virtually every digital transaction on the planet.",
  },
  {
    label: "Artificial Intelligence",
    body: "The handful of companies owning the foundational AI infrastructure of the future.",
  },
  {
    label: "Consumer Platforms",
    body: "Global brands with billions of loyal users and deep network lock-in.",
  },
  {
    label: "Financial Infrastructure",
    body: "Exchanges, clearing houses and data providers that own their entire category.",
  },
  {
    label: "Industrial Oligopolies",
    body: "Sectors where two or three global players control the entire supply chain.",
  },
];

const Philosophy = () => {
  return (
    <section
      id="philosophy"
      data-testid="philosophy-section"
      className="relative bg-[#111111] py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Eyebrow testId="philosophy-eyebrow">Investment Philosophy</Eyebrow>
          <h2
            data-testid="philosophy-heading"
            className="font-serif-display text-[#C9960C] text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-4xl"
          >
            NAG ANALYTIX — The Engine of Growth
          </h2>
          <p className="mt-6 max-w-3xl text-[#F8F6F0]/75 text-base sm:text-lg leading-relaxed">
            We invest in global monopolies — companies with exclusive control over more than 5–7 billion people globally. Whether there is a war, material price increase or any unforeseen contingency, a monopoly company can pass on prices to customers or distributors at will.
          </p>
        </Reveal>

        {/* Pillars */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-7">
          {PILLARS.map((p, idx) => (
            <Reveal key={p.title} delay={idx * 0.1}>
              <div
                data-testid={`pillar-card-${idx + 1}`}
                className="bg-[#1C1C1C] border border-[#C9960C]/40 border-t-2 border-t-[#C9960C] p-10 h-full"
              >
                <p className="text-[11px] tracking-[0.32em] uppercase text-[#C9960C]/70 mb-4">
                  {`0${idx + 1}`}
                </p>
                <h3 className="font-serif-display text-[#F5D485] text-3xl mb-4 tracking-tight">
                  {p.title}
                </h3>
                <p className="text-[#F8F6F0]/75 leading-relaxed">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Categories list */}
        <Reveal delay={0.2}>
          <div className="mt-20">
            <h3 className="font-serif-display text-[#F5D485] text-2xl sm:text-3xl mb-2">
              Where the Vault Invests
            </h3>
            <div className="h-px w-24 bg-[#C9960C] mb-10" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-2">
              {CATEGORIES.map((c, idx) => (
                <div
                  key={c.label}
                  data-testid={`category-${idx + 1}`}
                  className="group flex items-start gap-5 py-5 border-b border-[#C9960C]/15"
                >
                  <span className="font-serif-display text-[#C9960C]/70 text-xl w-10 shrink-0 pt-1">
                    {`0${idx + 1}`}
                  </span>
                  <div className="flex-1">
                    <h4 className="font-serif-display text-[#F5D485] text-xl mb-1">{c.label}</h4>
                    <p className="text-[#F8F6F0]/70 text-[15px] leading-relaxed">{c.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        {/* Pull quote */}
        <Reveal delay={0.3}>
          <div
            data-testid="philosophy-quote"
            className="mt-20 relative bg-[#1C1C1C] border border-[#C9960C] p-10 md:p-14"
          >
            <div className="absolute -top-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#F5D485] to-transparent" />
            <span className="font-serif-display text-[#C9960C]/30 text-7xl leading-none absolute top-4 left-6 select-none">
              &ldquo;
            </span>
            <p className="relative font-serif-display italic text-[#F5D485] text-2xl sm:text-3xl lg:text-4xl leading-snug text-center max-w-4xl mx-auto">
              Your Vault grows because we only own the world&apos;s most unassailable businesses.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Philosophy;
